/* eslint-disable no-param-reassign */
import React, { useState, useRef, ChangeEvent } from "react";

// region Imports - Language
import { GlobalMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
// endregion Imports - Language
// region Imports - Styles
import Styled from "./styles";
// endregion Imports - Styles

interface ImageUploaderSquareProps {
  imgPath: string;
  allowedExtension: ".ico" | ".png" | ".svg";
  imgFallbackPath?: string;
  colorImgBackground?: string;
  handleEdit: (event: ChangeEvent<HTMLInputElement>) => void;
  handleHelp: () => void;
}

const ImageUploaderSquare: React.FC<ImageUploaderSquareProps> = ({
  imgPath,
  allowedExtension,
  imgFallbackPath,
  colorImgBackground,
  handleEdit,
  handleHelp
}) => {
  // region States
  const [showOptions, setShowOptions] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [triedFallback, setTriedFallback] = useState(false);

  const inputFileRef = useRef<HTMLInputElement>(null);
  // endregion States
  // region Hooks
  const { t } = useTranslation();
  // endregion Hooks
  // region Handle Functions

  const handleImageClick = () => {
    setShowOptions((prev) => !prev);
  };

  const handleEditClick = () => {
    setShowOptions(false);
    inputFileRef.current?.click();
  };

  const handleViewClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setShowOptions(false);
    setShowModal(true);
  };

  const handleHelpClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setShowOptions(false);
    handleHelp();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleEdit(event);

    const file = event.target.files?.[0];

    setSelectedFileName(file?.name ?? "");
  };

  const handleErrorImgPath = () => {
    if (!triedFallback) setTriedFallback(true);
  };
  // endregion Handle Functions

  return (
    <Styled.Container>
      <Styled.ImageContainer
        isFileChosen={Boolean(selectedFileName)}
        backgroundColor={colorImgBackground}
        onClick={handleImageClick}
      >
        <img
          src={triedFallback ? imgFallbackPath : imgPath}
          onError={handleErrorImgPath}
          alt="editable"
        />
        {showOptions && (
          <Styled.OptionsContainer>
            <Styled.OptionButton type="button" onClick={handleEditClick}>
              {t(GlobalMessages.buttonEdit)}
            </Styled.OptionButton>
            <Styled.OptionButton type="button" onClick={handleViewClick}>
              {t(GlobalMessages.buttonView)}
            </Styled.OptionButton>
            <Styled.OptionButton type="button" onClick={handleHelpClick}>
              {t(GlobalMessages.buttonHelp)}
            </Styled.OptionButton>
          </Styled.OptionsContainer>
        )}
        <input
          type="file"
          ref={inputFileRef}
          onChange={handleFileChange}
          accept={allowedExtension}
          style={{ display: "none" }}
        />
      </Styled.ImageContainer>
      <Styled.FileName>{selectedFileName}</Styled.FileName>

      {showModal && (
        <Styled.ModalContainer onClick={() => setShowModal(false)}>
          <Styled.ModalImage
            src={imgPath}
            percentageSizeScreen={allowedExtension === ".ico" ? 40 : 80}
            alt="visualizable"
          />
        </Styled.ModalContainer>
      )}
    </Styled.Container>
  );
};

ImageUploaderSquare.defaultProps = {
  imgFallbackPath: "",
  colorImgBackground: undefined
};

export default ImageUploaderSquare;
